import { inject } from '@angular/core';
import { ResolveFn, Routes } from '@angular/router';

import { HomeComponent } from '@callcenter-app/pages/home/home.component';
import { CallCenterComponent } from '@callcenter-app/pages/call-center/call-center.component';
import { OrderViewComponent } from '@callcenter-app/pages/order/order-view/order-view.component';
import { CallsListComponent } from '@callcenter-app/pages/calls-list/calls-list.component';
import { CallsReportsComponent } from '@callcenter-app/pages/report/calls-reports/calls-reports.component';
import { CallsStatusReportComponent } from '@callcenter-app/pages/report/calls-status-report/calls-status-report.component';
import { CallsHoursReportComponent } from '@callcenter-app/pages/report/calls-hours-report/calls-hours-report.component';
import { OperatorOrderStatReportComponent } from '@callcenter-app/pages/report/operator-order-stat/operator-order-stat.component';
import { FeedbackRatingReportComponent } from '@callcenter-app/pages/report/feedback-rating/feedback-rating-report.component';
import { HxOrderService, OrderResponse } from 'hx-services';
import { UserListComponent } from '@callcenter-app/pages/user/user-list/user-list.component';
import { UserEditComponent } from '@callcenter-app/pages/user/user-edit/user-edit.component';
import { orderRoutes } from 'hx-order';
import { monitoringReportGuard } from '@callcenter-app/service/monitoring-report.guard';
import { oldEditorReturnGuard } from '@callcenter-app/service/old-editor-return.guard';
import { canLeaveBookedPage } from '@callcenter-app/pages/order-editor/booked/order-booked-leave.guard';
import { canLeaveEditPage } from '@callcenter-app/pages/order-editor/edit/order-edit-leave.guard';
import { canLeaveNewPage } from '@callcenter-app/pages/order-editor/new/order-new-leave.guard';
import { WORKPLACE_READY } from '@callcenter-app/service/workplace.config';
import { THEMED_PAGE } from '@callcenter-app/shell/shell.config';

const orderResolveFn: ResolveFn<OrderResponse> = route => inject(HxOrderService)
    .getFullOrderById(Number(route.paramMap.get('id')));

// `data: THEMED_PAGE` — a new page that follows the theme of the call-centre; a route without it is light whatever the theme (the old pages on Metronic, the shared pages of the libraries).
export const ROUTES: Routes = [
  {
    path: '',
    component: HomeComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'call-center'
      },
      // The workplace (stage 4). While `WORKPLACE_READY` is off `/call-center` is the page it always was and `/call-center-old` does not exist.
      ...(WORKPLACE_READY ? [
        {
          path: 'call-center',
          loadComponent: () => import('@callcenter-app/pages/workplace/workplace-page.component').then(m => m.WorkplacePageComponent),
          data: THEMED_PAGE,
        },
        {
          // the old page, unchanged: its own plaque of the call, its own window for the number of the operator
          path: 'call-center-old',
          component: CallCenterComponent,
        },
      ] : [
        {
          path: 'call-center',
          component: CallCenterComponent,
        },
      ]),
      {
        path: 'clients',
        loadComponent: () => import('hx-component').then(m => m.HxClientDirectoryComponent)
      },
      {
        path: 'clients-old',
        loadComponent: () => import('hx-component').then(m => m.HxClientListComponent)
      },
      {
        // The new «Заказы» page (current orders). Only the bare `/orders`: the old list keeps `/orders/list` (sidebar
        // «Заказы (old)»), the editor `/orders/edit`, the old view `/orders/:id`, `/orders/archive`, `/orders/feedbacks`.
        path: 'orders',
        pathMatch: 'full',
        loadComponent: () => import('@callcenter-app/pages/orders/orders.component').then(m => m.OrdersComponent),
        data: THEMED_PAGE,
      },
      {
        path: 'orders',
        children: orderRoutes
      },
      {
        // The old view. Its one change (1d): an operator the NEW «Заказы» page or card sent to the old editor is taken back from here to the
        // list / the card instead (the editor's «Сохранить» ends in `/orders/:id`) — everybody else passes the guard as before.
        path: 'orders/:id',
        component: OrderViewComponent,
        pathMatch: 'full',
        canActivate: [oldEditorReturnGuard],
        resolve: {
          order: orderResolveFn,
        }
      },
      {
        path: 'orders-archive',
        loadComponent: () => import('@callcenter-app/pages/orders-archive/orders-archive.component').then(m => m.OrdersArchiveComponent),
        data: THEMED_PAGE,
      },
      {
        // «Новый заказ», step 1 (stage 2, 2a-1). BEFORE `order/:id`: else `new` is read as an order id. 
        path: 'order/new',
        pathMatch: 'full',
        loadComponent: () => import('@callcenter-app/pages/order-editor/new/order-new-page.component').then(m => m.OrderNewPageComponent),
        data: THEMED_PAGE,
        // with a booking held («Изменить» of «Когда и где» on step 2) the page asks before it is left; without one it is left at once
        canDeactivate: [canLeaveNewPage]
      },
      {
        // «Новый заказ», step 2 (stage 2, 2b): the booking `cartId` is held, the order is made of it. A live booking asks before the page is left.
        path: 'order/new/:cartId',
        loadComponent: () => import('@callcenter-app/pages/order-editor/booked/order-booked-page.component').then(m => m.OrderBookedPageComponent),
        data: THEMED_PAGE,
        canDeactivate: [canLeaveBookedPage]
      },
      {
        // «Изменение заказа» (stage 3, 3a): the change of an order in work, from «Изменить» of the shutter and of the card. BEFORE `order/:id`, which is the card. The page asks before it is left with something to lose.
        path: 'order/:id/edit',
        pathMatch: 'full',
        loadComponent: () => import('@callcenter-app/pages/order-editor/edit/order-edit-page.component').then(m => m.OrderEditPageComponent),
        data: THEMED_PAGE,
        canDeactivate: [canLeaveEditPage]
      },
      {
        // New full order-card (phase 1) — view + the actions that exist for final orders; reuses the
        // /api/vn/orders-archive/** endpoints (serve any order in cc scope, incl. in-progress). Does
        // NOT replace `orders/:id` (the old view keeps editing/cancel/kaspi for in-progress orders).
        path: 'order/:id',
        loadComponent: () => import('@callcenter-app/pages/order-card/order-card-page.component').then(m => m.OrderCardPageComponent),
        data: THEMED_PAGE,
      },
      {
        path: 'calls',
        component: CallsListComponent,
      },
      {
        path: 'calls-new',
        loadComponent: () => import('@callcenter-app/pages/calls-new/calls-new.component').then(m => m.CallsNewComponent),
        data: THEMED_PAGE,
      },
      {
        path: 'feedbacks-new',
        loadComponent: () => import('@callcenter-app/pages/feedbacks-new/feedbacks-new.component').then(m => m.FeedbacksNewComponent),
        data: THEMED_PAGE,
      },
      {
        path: 'rejections',
        loadComponent: () => import('hx-component').then(m => m.HxRejectionListComponent)
      },
      {
        path: 'users',
        children: [
          {
            path: '',
            component: UserListComponent,
          },
          {
            path: 'new',
            component: UserEditComponent,
          },
          {
            path: ':id/edit',
            component: UserEditComponent,
          },
        ]
      },
      {
        path: 'report',
        children: [
          {
            path: 'calls',
            component: CallsReportsComponent,
          },
          {
            path: 'calls-status',
            component: CallsStatusReportComponent,
          },
          {
            path: 'calls-hours',
            component: CallsHoursReportComponent
          },
          {
            path: 'operator-order-stat',
            component: OperatorOrderStatReportComponent
          },
          {
            // Право то же, что у «Мониторинга» и у эндпоинтов сводки: без него страница пустая.
            path: 'operator-summary',
            canActivate: [monitoringReportGuard],
            loadComponent: () => import('@callcenter-app/pages/report/operator-summary/operator-summary.component').then(m => m.OperatorSummaryComponent),
            data: THEMED_PAGE,
          },
          {
            path: 'feedback-rating',
            component: FeedbackRatingReportComponent
          },
          {
            path: 'monitoring',
            canActivate: [monitoringReportGuard],
            loadComponent: () => import('@callcenter-app/pages/report/monitoring/monitoring.component').then(m => m.MonitoringComponent),
            data: THEMED_PAGE,
          },
          {
            path: 'call-funnel',
            canActivate: [monitoringReportGuard],
            loadComponent: () => import('@callcenter-app/pages/report/call-funnel/call-funnel-report.component').then(m => m.CallFunnelReportComponent),
            data: THEMED_PAGE,
          },
          {
            path: 'load',
            canActivate: [monitoringReportGuard],
            loadComponent: () => import('@callcenter-app/pages/report/call-load/call-load.component').then(m => m.CallLoadComponent),
            data: THEMED_PAGE,
          },
        ]
      },
      {
        // Собственный результат оператора. Гварда нет намеренно: бэкенд подставляет оператора
        // из токена, чужие данные страница отдать не может (ReportBean#getMonitoringMySales).
        path: 'my-sales',
        loadComponent: () => import('@callcenter-app/pages/my-sales/my-sales.component').then(m => m.MySalesComponent),
        data: THEMED_PAGE,
      },
      {
        path: 'faq',
        pathMatch: 'full',
        loadComponent: () => import('@callcenter-app/pages/faq/faq.component').then(m => m.FaqComponent),
        data: THEMED_PAGE,
      },
      {
        path: 'faq/list',
        loadComponent: () => import('hx-component').then(m => m.HxFaqListComponent)
      },
    ]
  },
  {
    path: '**',
    redirectTo: 'call-center'
  }
];
